/**
 * OutfitEngine - Nouveau moteur de génération d'outfits (v2)
 *
 * Principes :
 *  1. Outfits UNIQUES (pas de doublons d'items)
 *  2. Matching INTELLIGENT : couleurs + formalité + matière + saison + style
 *  3. SCORES MULTI-TAGS : chaque outfit a un % match avec plusieurs tags
 *  4. Seuil 75% pour apparaître dans un onglet
 *  5. Météo-aware : adapté à la température du jour
 *
 * Sortie : [ { id, items, score, cohesion, tagMatches: { chic: 0.92, minimaliste: 0.85 }, ... } ]
 */

// ═══ TABLE DE COMPATIBILITÉ COULEURS ═══
// Règles classiques du style : qu'est-ce qui va avec quoi ?
const COLOR_PALETTE = {
  noir:      { neutral: true, goesWith: ['blanc', 'gris', 'beige', 'rouge', 'camel', 'marine', 'doré', 'argent', 'rose', 'nude'], contrast: 'blanc' },
  blanc:     { neutral: true, goesWith: ['noir', 'gris', 'beige', 'marine', 'bleu', 'rouge', 'camel'], contrast: 'noir' },
  gris:      { neutral: true, goesWith: ['noir', 'blanc', 'rose', 'bleu', 'rouge', 'jaune', 'vert'], contrast: 'noir' },
  beige:     { neutral: true, goesWith: ['noir', 'blanc', 'marron', 'camel', 'crème', 'kaki', 'vert'], contrast: 'marron' },
  crème:     { neutral: true, goesWith: ['beige', 'camel', 'marron', 'bordeaux', 'marine'], contrast: 'marron' },
  marine:    { neutral: true, goesWith: ['blanc', 'beige', 'rouge', 'jaune', 'camel', 'gris'], contrast: 'blanc' },
  camel:     { neutral: false, goesWith: ['noir', 'blanc', 'beige', 'crème', 'marron', 'marine'], contrast: 'noir' },
  marron:    { neutral: false, goesWith: ['beige', 'crème', 'camel', 'vert', 'jaune', 'noir'], contrast: 'crème' },
  rouge:     { neutral: false, goesWith: ['noir', 'blanc', 'marine', 'gris', 'beige'], contrast: 'blanc' },
  bordeaux:  { neutral: false, goesWith: ['beige', 'crème', 'marine', 'gris', 'noir', 'camel'], contrast: 'crème' },
  rose:      { neutral: false, goesWith: ['gris', 'blanc', 'beige', 'noir', 'nude'], contrast: 'gris' },
  nude:      { neutral: true, goesWith: ['noir', 'blanc', 'beige', 'doré', 'bordeaux', 'marine'], contrast: 'noir' },
  bleu:      { neutral: false, goesWith: ['blanc', 'marine', 'gris', 'beige', 'jaune'], contrast: 'blanc' },
  vert:      { neutral: false, goesWith: ['blanc', 'beige', 'marron', 'noir', 'camel'], contrast: 'blanc' },
  kaki:      { neutral: false, goesWith: ['blanc', 'beige', 'noir', 'marron', 'camel'], contrast: 'blanc' },
  jaune:     { neutral: false, goesWith: ['blanc', 'gris', 'marine', 'noir', 'bleu'], contrast: 'noir' },
  doré:      { neutral: false, goesWith: ['noir', 'blanc', 'nude', 'bordeaux', 'marine'], contrast: 'noir' },
  argent:    { neutral: false, goesWith: ['noir', 'blanc', 'gris', 'marine'], contrast: 'noir' },
  violet:    { neutral: false, goesWith: ['blanc', 'gris', 'noir', 'rose'], contrast: 'blanc' },
  orange:    { neutral: false, goesWith: ['blanc', 'marine', 'beige', 'marron'], contrast: 'marine' },
};

// Normalise une couleur en tag interne (gestion synonymes français/anglais)
function normalizeColor(color) {
  if (!color) return null;
  const c = color.toLowerCase().trim();
  const map = {
    'black': 'noir', 'white': 'blanc', 'grey': 'gris', 'gray': 'gris',
    'navy': 'marine', 'red': 'rouge', 'brown': 'marron', 'cream': 'crème',
    'pink': 'rose', 'blue': 'bleu', 'green': 'vert', 'khaki': 'kaki',
    'yellow': 'jaune', 'gold': 'doré', 'silver': 'argent',
    'purple': 'violet', 'orange': 'orange',
    'burgundy': 'bordeaux',
  };
  // Match direct ou via map
  if (COLOR_PALETTE[c]) return c;
  if (map[c]) return map[c];
  // Fuzzy match (contient le mot)
  for (const key of Object.keys(COLOR_PALETTE)) {
    if (c.includes(key)) return key;
  }
  for (const [en, fr] of Object.entries(map)) {
    if (c.includes(en)) return fr;
  }
  return null;
}

// ─── Score de compatibilité couleurs d'une tenue (0-1) ───
//
// v1.12.58 (Phase B) : matching couleur multi-niveau.
//
// Avant : ne lisait que `i.color || ai_metadata.mainColor` (bug — l'IA écrit
//   `primaryColor`, pas `mainColor`) et ne comparait que les couleurs primaires.
//
// Maintenant : exploite tous les niveaux générés par Claude Vision :
//   - primaryColor : couleur dominante (ex: "vert")
//   - colorDetail  : nuance précise (ex: "menthe", "olive", "sapin")
//   - secondaryColors : 0-3 couleurs secondaires (motifs, contrastes)
//   - colorTone    : "chaud" | "froid" | "neutre"
//
// Algorithme par paire d'items (a, b) :
//   1. Same primaryColor + same colorDetail → 1.00 (total look exact)
//   2. Same primaryColor + different colorDetail → 0.85 (vert+vert mais nuances diff)
//   3. Different primary, mais palette goesWith → 1.00
//   4. Different primary, l'une neutre → 0.70
//   5. Different primary, ni neutre ni goesWith → 0.30 (dissonant)
//   + Bonus +0.10 si secondaryColors créent un pont (ex: B a "marine" en
//     secondaire et A a primary "marine" → cohérence visuelle voulue)
//   - Pénalité −0.10 si colorTone mismatch chaud/froid (mais pas si neutre)
function scoreColorHarmony(items) {
  // Extraction multi-niveau pour chaque item
  const itemsColor = items
    .map(i => {
      const meta = i.ai_metadata || {};
      // Fix bug : lire primaryColor (AI écrit ce champ) avant fallback legacy
      const primary = normalizeColor(meta.primaryColor || meta.mainColor || i.color);
      if (!primary) return null;
      return {
        primary,
        detail: meta.colorDetail ? normalizeColor(meta.colorDetail) : null,
        secondaries: Array.isArray(meta.secondaryColors)
          ? meta.secondaryColors.map(c => normalizeColor(c)).filter(Boolean)
          : [],
        tone: meta.colorTone || null, // 'chaud' | 'froid' | 'neutre'
      };
    })
    .filter(Boolean);

  if (itemsColor.length < 2) return 0.7;

  let totalScore = 0;
  let totalPairs = 0;

  for (let i = 0; i < itemsColor.length; i++) {
    for (let j = i + 1; j < itemsColor.length; j++) {
      totalPairs++;
      const a = itemsColor[i];
      const b = itemsColor[j];

      // ─── Score base sur primaryColor ───
      let pairScore;
      if (a.primary === b.primary) {
        // Même couleur primaire — bonus si nuance (colorDetail) identique
        pairScore = (a.detail && b.detail && a.detail === b.detail) ? 1.0 : 0.85;
      } else {
        const palA = COLOR_PALETTE[a.primary];
        if (palA && palA.goesWith.includes(b.primary)) pairScore = 1.0;
        else if (palA && palA.neutral) pairScore = 0.7;
        else if (COLOR_PALETTE[b.primary]?.neutral) pairScore = 0.7;
        else pairScore = 0.3;
      }

      // ─── Bonus secondaryColors qui créent un pont ───
      // Si A a "marine" en secondaire et B est "marine" en primaire,
      // c'est un signal fort que les 2 items ont été pensés ensemble.
      const aSecMatchesB = a.secondaries.some(s => s === b.primary);
      const bSecMatchesA = b.secondaries.some(s => s === a.primary);
      if (aSecMatchesB || bSecMatchesA) {
        pairScore = Math.min(1.0, pairScore + 0.1);
      }

      // ─── Pénalité tonalité chaud + froid ───
      // (neutre ne pénalise jamais — tolérant par nature)
      if (a.tone && b.tone
          && a.tone !== 'neutre' && b.tone !== 'neutre'
          && a.tone !== b.tone) {
        pairScore = Math.max(0, pairScore - 0.1);
      }

      totalScore += pairScore;
    }
  }

  const finalScore = totalPairs === 0 ? 0.7 : totalScore / totalPairs;
  return Math.max(0, Math.min(1, finalScore));
}

// ─── v1.12.69 (Phase D.3) : Score de compatibilité matières (pair-wise) ───
//
// Familles de matières — basée sur registre stylistique :
//   - casual    : coton, denim, jersey, lin, mailles, sweat, molleton
//   - chic      : soie, cachemire, laine, satin, velours, cuir, tweed, gabardine, mohair
//   - sport     : polyester, polyamide, néoprène, élasthanne, mesh, nylon, lycra
//
// Règles pair-wise :
//   - Même matière             → 0.95
//   - Même famille             → 0.90
//   - casual + chic            → 0.70 (smart-casual, mix réussi)
//   - casual + sport           → 0.60 (athleisure tolérance)
//   - chic + sport             → 0.30 (mismatch registres)
//   - Pas d'info OU famille inconnue → 0.70 (neutre)
const MATERIAL_FAMILIES = {
  casual: ['coton', 'denim', 'jean', 'jersey', 'lin', 'maille', 'sweat', 'molleton', 'chambray'],
  chic:   ['soie', 'cachemire', 'laine', 'satin', 'velours', 'cuir', 'daim', 'suède', 'nubuck',
           'tweed', 'gabardine', 'mohair', 'organza', 'crepe', 'crêpe', 'mohair', 'alpaga', 'angora'],
  sport:  ['polyester', 'polyamide', 'néoprène', 'neoprene', 'élasthanne', 'elasthanne',
           'mesh', 'nylon', 'lycra', 'spandex'],
};

function getMaterialFamily(material) {
  if (!material) return null;
  const m = material.toLowerCase().trim();
  if (!m) return null;
  for (const [family, list] of Object.entries(MATERIAL_FAMILIES)) {
    if (list.some(token => m.includes(token) || token.includes(m))) return family;
  }
  return null;
}

function materialPairScore(matA, matB) {
  if (!matA || !matB) return 0.7;
  if (matA === matB) return 0.95;
  const famA = getMaterialFamily(matA);
  const famB = getMaterialFamily(matB);
  if (!famA || !famB) return 0.7;
  if (famA === famB) return 0.9;

  const combo = [famA, famB].sort().join('+');
  const compat = {
    'casual+chic':  0.7,
    'casual+sport': 0.6,
    'chic+sport':   0.3,
  };
  return compat[combo] ?? 0.7;
}

function scoreMaterialHarmony(items) {
  const materials = items
    .map(i => (i.ai_metadata?.material || '').toLowerCase().trim())
    .filter(Boolean);
  if (materials.length < 2) return 0.7;

  let total = 0;
  let pairs = 0;
  for (let i = 0; i < materials.length; i++) {
    for (let j = i + 1; j < materials.length; j++) {
      total += materialPairScore(materials[i], materials[j]);
      pairs++;
    }
  }
  return pairs === 0 ? 0.7 : total / pairs;
}

// ─── Score de cohérence formelle (tous les items doivent être à niveau de formalité similaire) ───
//
// v1.12.26 : ajout d'un GARDE-FOU strict sur l'écart maximum entre items.
// Avant : un chapeau formality 2/10 dans une tenue chic 8/10 produisait un
//   score std≈2.77 → 0.65 (pénalité légère) → outfit accepté quand même.
// Maintenant : si l'écart entre l'item le moins formel et le plus formel est
//   > MAX_FORMALITY_GAP, on retourne 0 → outfit rejeté complètement.
//
// MAX_FORMALITY_GAP = 4 (équilibré) :
//   - 2/10 + 8/10 = écart 6 → REJET
//   - 4/10 + 7/10 = écart 3 → accepté avec score 0.65
//   - 6/10 + 8/10 = écart 2 → accepté avec score 0.85
// v1.12.81 : 6 → 7 — encore élargi. User report : trop d'articles compatibles
// invisibles dans Looks. Gap=7 permet par ex. sneakers (formalité 2) avec
// chemise élégante (formalité 8) — un look casual-chic plausible.
// v1.12.440 — aligné sur backend CDK : 7 → 3 après remontée user "chemise
// midnight 6 + short 2 = gap 4 incohérent". Gap=3 autorise les transitions
// raisonnables (5+8, 4+7), interdit casual+habillé.
const MAX_FORMALITY_GAP = 3;

function scoreFormality(items) {
  const formalities = items
    .map(i => typeof i.ai_metadata?.formality === 'number' ? i.ai_metadata.formality : null)
    .filter(f => f !== null);
  if (formalities.length < 2) return 0.7;

  // v1.12.26 : Garde-fou écart maximal
  // Si un item est très en-dessous ou au-dessus du reste de l'outfit
  // (ex: chapeau casual 2/10 avec robe chic 8/10), c'est incohérent.
  const minF = Math.min(...formalities);
  const maxF = Math.max(...formalities);
  const gap = maxF - minF;
  if (gap > MAX_FORMALITY_GAP) return 0;

  const avg = formalities.reduce((a, b) => a + b, 0) / formalities.length;
  const variance = formalities.reduce((sum, f) => sum + Math.pow(f - avg, 2), 0) / formalities.length;
  const std = Math.sqrt(variance);

  // Std faible = tenue cohérente, std forte = mix casual/formel = pas cohérent
  if (std < 1) return 1.0;
  if (std < 2) return 0.85;
  if (std < 3) return 0.65;
  return 0.4; // incohérence forte
}

// ─── Score de compatibilité saison/température ───
// ═══ v1.12.25 : scoreWeather utilise maintenant temperatureRange IA ═══
//
// Avant : seulement regex sur le nom + matière (rate les chaussures
// hivernales sans "bottes" dans le nom, etc.).
//
// Maintenant : priorité à ai_metadata.temperatureRange [min, max] qui est
// précis pour 100% du catalogue analysé. Fallback regex pour les items
// pas encore analysés (dressing personnel récent).
//
// Logique de pénalité progressive :
//   - tempC dans [min, max]                      → pas de pénalité (idéal)
//   - tempC dans [min-3, min[ ou ]max, max+3]    → pénalité légère −0.05
//   - tempC dans [min-8, min-3[ ou ]max+3, max+8] → pénalité forte −0.20
//   - tempC < min-8 ou > max+8                    → pénalité critique −0.35
//
// Exemples concrets à 19°C :
//   - Robe [12, 22]    : 19 ∈ [12, 22] → pas de pénalité
//   - Chaussures [-5, 10] : 19 > 10+8=18 → pénalité critique −0.35
//   - Sandales [22, 35]  : 19 ∈ [22-3, 22[ → pénalité légère −0.05
function scoreWeather(items, tempC) {
  if (typeof tempC !== 'number') return 0.8;

  let score = 1.0;

  for (const item of items) {
    const range = item?.ai_metadata?.temperatureRange;
    const hasRange = Array.isArray(range)
      && range.length === 2
      && typeof range[0] === 'number'
      && typeof range[1] === 'number'
      && !(range[0] === 0 && range[1] === 0); // [0,0] = pas calculé par IA

    if (hasRange) {
      const [min, max] = range;
      if (tempC >= min && tempC <= max) {
        // Dans la plage → 0 pénalité
        continue;
      } else if (tempC >= min - 3 && tempC <= max + 3) {
        // Marge ±3° → pénalité légère
        score -= 0.05;
      } else if (tempC >= min - 8 && tempC <= max + 8) {
        // Marge ±8° → pénalité forte
        score -= 0.20;
      } else {
        // Hors plage de plus de 8° → pénalité critique
        score -= 0.35;
      }
      continue; // priorité IA, on saute le fallback regex pour cet item
    }

    // ═══ FALLBACK regex pour items SANS temperatureRange IA ═══
    // (cas rares : dressing personnel pas encore analysé)
    const name = (item.name || '').toLowerCase();
    const material = (item?.ai_metadata?.material || '').toLowerCase();

    const isHot = /manteau|doudoune|pull\s*épais|parka|cachemire|laine|bottes?\s*(montantes|hautes)/.test(name)
      || /laine|cachemire|mohair/.test(material);
    if (isHot && tempC > 22) score -= 0.30;

    const isCold = /robe\s*sans\s*manches|short|bermuda|maillot|sandale|tongs|dos\s*nu|crop[- ]?top|brassière/.test(name);
    if (isCold && tempC < 12) score -= 0.30;
  }

  // Manque veste quand il fait froid
  if (tempC < 15) {
    const hasVeste = items.some(i => i.category === 'Vestes');
    if (!hasVeste) score -= 0.2;
  }

  return Math.max(0, Math.min(1, score));
}

// Helper exposé pour identifier les items inadaptés (warnings UI)
// Retourne un array d'items avec leur niveau d'inadéquation
//   { item, severity: 'severe'|'moderate'|'mild', message }
export function getWeatherIssues(items, tempC) {
  if (typeof tempC !== 'number' || !Array.isArray(items)) return [];
  const issues = [];

  for (const item of items) {
    const range = item?.ai_metadata?.temperatureRange;
    const hasRange = Array.isArray(range)
      && range.length === 2
      && typeof range[0] === 'number'
      && typeof range[1] === 'number'
      && !(range[0] === 0 && range[1] === 0);

    if (!hasRange) continue;
    const [min, max] = range;
    if (tempC >= min && tempC <= max) continue; // OK

    let severity, message;
    const tooHot = tempC > max;
    const tooCold = tempC < min;
    const gap = tooHot ? tempC - max : min - tempC;

    if (gap > 8) {
      severity = 'severe';
      message = tooHot
        ? `Trop chaud aujourd'hui (${tempC}°C, max ${max}°C)`
        : `Trop froid aujourd'hui (${tempC}°C, min ${min}°C)`;
    } else if (gap > 3) {
      severity = 'moderate';
      message = tooHot
        ? `Un peu chaud (${tempC}°C, idéal jusqu'à ${max}°C)`
        : `Un peu frais (${tempC}°C, idéal à partir de ${min}°C)`;
    } else {
      severity = 'mild';
      message = tooHot
        ? `Limite chaud (${tempC}°C vs ${max}°C max)`
        : `Limite frais (${tempC}°C vs ${min}°C min)`;
    }
    issues.push({ item, severity, message });
  }

  return issues;
}

// ─── Score de compatibilité style (les items partagent-ils des tags ?) ───
// v1.10.84 : Score de cohérence stylistique basé sur styleScores IA
//
// AVANT : ne regardait que les styleTags (mots-clés grossiers, souvent absents).
//   → Un sac casual + robe chic pouvaient avoir un tag commun "polyvalent"
//     → coh acceptée → outfit incohérent publié.
//
// MAINTENANT :
//   1. On collecte tous les styleScores (chic, sport, casual, romantique, etc.)
//   2. Pour chaque axe de style, on calcule le score MIN parmi les items
//      → Si tous les items ont styleScores.chic >= 50, on considère qu'ils
//        partagent un style chic fort (cohérent).
//   3. Le score final = max sur tous les axes du score min (= "tous d'accord").
//   4. Si un targetTag est fourni, on vérifie aussi l'alignement avec ce tag.
//
// Exemple :
//   Robe : { chic: 90, sport: 10, casual: 30 }
//   Sac  : { chic: 20, sport: 80, casual: 50 } ← sac de voyage casual
//   Min sur axe chic = min(90, 20) = 20 → coh faible (incohérent) ✓
//   Min sur axe sport = min(10, 80) = 10 → faible
//   Min sur axe casual = min(30, 50) = 30 → moyen
//   Score = max(20, 10, 30) / 100 = 0.30 → REJETÉ par le seuil 0.4
function scoreStyleCoherence(items, targetTag = null) {
  if (!items || items.length < 2) return 0.7; // outfit trop petit, on accepte

  // Collecter tous les styleScores
  const styleScoresList = [];
  let itemsWithScores = 0;
  for (const it of items) {
    const ss = it.ai_metadata?.styleScores;
    if (ss && typeof ss === 'object' && Object.keys(ss).length > 0) {
      styleScoresList.push(ss);
      itemsWithScores++;
    } else {
      // Item sans styleScores → on push un objet vide pour conserver la structure
      styleScoresList.push({});
    }
  }

  // Si moins de 2 items ont des styleScores, fallback sur les styleTags (ancienne logique)
  if (itemsWithScores < 2) {
    const allTags = new Set();
    const tagLists = [];
    for (const it of items) {
      const tags = Array.isArray(it.ai_metadata?.styleTags)
        ? it.ai_metadata.styleTags.map(t => (t || '').toLowerCase())
        : [];
      if (tags.length > 0) {
        tagLists.push(new Set(tags));
        for (const t of tags) allTags.add(t);
      }
    }
    if (tagLists.length < 2) return 0.6; // pas assez de data, on accepte un peu moins haut
    let sharedTags = 0;
    for (const tag of allTags) {
      const count = tagLists.filter(s => s.has(tag)).length;
      if (count >= 2) sharedTags++;
    }
    if (sharedTags === 0) return 0.4; // était 0.5, on est plus strict
    if (sharedTags === 1) return 0.7;
    return 0.85 + Math.min(0.1, sharedTags * 0.02);
  }

  // Calculer pour chaque axe de style le score MINIMUM parmi les items
  // qui ont une note sur cet axe (au moins itemsWithScores - 1 items)
  const allAxes = new Set();
  for (const ss of styleScoresList) {
    for (const k of Object.keys(ss)) allAxes.add(k.toLowerCase());
  }

  let bestMinScore = 0; // le meilleur axe consensuel
  for (const axis of allAxes) {
    let minOnAxis = 100;
    let countWithData = 0;
    for (const ss of styleScoresList) {
      // Récupérer le score sur cet axe (lookup case-insensitive)
      let score = null;
      for (const k of Object.keys(ss)) {
        if (k.toLowerCase() === axis) { score = ss[k]; break; }
      }
      if (typeof score === 'number') {
        countWithData++;
        if (score < minOnAxis) minOnAxis = score;
      }
    }
    // Ne considérer cet axe que si au moins 75% des items ont une note dessus
    if (countWithData >= itemsWithScores * 0.75) {
      if (minOnAxis > bestMinScore) bestMinScore = minOnAxis;
    }
  }

  // Score base : on convertit le score min sur le meilleur axe en 0-1
  // Si tous les items ont >= 60 sur "chic" → bestMinScore = 60 → coh = 0.6
  // Si tous les items ont >= 80 sur "sport" → bestMinScore = 80 → coh = 0.8
  let coherence = bestMinScore / 100;

  // Si un targetTag est fourni, on bonus/malus selon l'alignement
  if (targetTag) {
    const targetLower = targetTag.toLowerCase();
    // Vérifier si tous les items ont styleScores[targetTag] >= 40
    let alignedCount = 0;
    for (const ss of styleScoresList) {
      let sc = null;
      for (const k of Object.keys(ss)) {
        if (k.toLowerCase() === targetLower) { sc = ss[k]; break; }
      }
      if (typeof sc === 'number' && sc >= 40) alignedCount++;
    }
    const alignRatio = itemsWithScores > 0 ? alignedCount / itemsWithScores : 0;
    if (alignRatio >= 0.75) {
      coherence = Math.max(coherence, 0.85); // outfit aligné sur target → bon
    } else if (alignRatio < 0.4) {
      coherence = Math.min(coherence, 0.35); // outfit pas aligné → pénalisé
    }
  }

  return coherence;
}

// ─── COHESION SCORE GLOBALE (combinaison pondérée des sous-scores) ───
// v1.10.84 : ajout targetTag pour aligner la cohésion stylistique sur la préférence user
// v1.12.61 (Phase C.3) : ajout colorBank optionnel comme 5e facteur (10% weight).
//   Si bank fournie : bankColor = moyenne des scoreItemColor() sur les items.
//   Donne un bonus aux outfits dont CHAQUE item a une couleur "validée"
//   par les recettes hebdo de Claude pour sa (catégorie, sous-catégorie).
//   Si bank absente : facteur neutre (0.7) → impact minimal.
export function computeCohesion(items, tempC, targetTag = null, colorBank = null, materialBank = null) {
  const colorS = scoreColorHarmony(items);
  const formS = scoreFormality(items);
  const weatherS = scoreWeather(items, tempC);
  const styleS = scoreStyleCoherence(items, targetTag);
  const materialPairS = scoreMaterialHarmony(items); // v1.12.69 — pair-wise materials

  // ═══ Phase C.3 : Bank color score (moyenne sur tous items) ═══
  let bankColorS = 0.7;
  let bankColorBreakdown = null;
  if (colorBank?.byKey && Array.isArray(items) && items.length > 0) {
    try {
      const { scoreItemColor } = require('./ColorBankService');
      const itemScores = items.map(it => scoreItemColor(it, colorBank));
      const validScores = itemScores.map(s => s.score).filter(n => typeof n === 'number');
      if (validScores.length > 0) {
        bankColorS = validScores.reduce((a, b) => a + b, 0) / validScores.length;
        bankColorBreakdown = {
          mean: bankColorS,
          perItem: itemScores.map((s, i) => ({
            id: items[i]?.id,
            category: items[i]?.category,
            score: s.score,
            reason: s.reason,
          })),
        };
      }
    } catch (e) {}
  }

  // ═══ Phase D.3 : Bank material score (moyenne sur tous items) ═══
  let bankMaterialS = 0.7;
  let bankMaterialBreakdown = null;
  if (materialBank?.byKey && Array.isArray(items) && items.length > 0) {
    try {
      const { scoreItemMaterial } = require('./MaterialBankService');
      const itemScores = items.map(it => scoreItemMaterial(it, materialBank));
      const validScores = itemScores.map(s => s.score).filter(n => typeof n === 'number');
      if (validScores.length > 0) {
        bankMaterialS = validScores.reduce((a, b) => a + b, 0) / validScores.length;
        bankMaterialBreakdown = {
          mean: bankMaterialS,
          perItem: itemScores.map((s, i) => ({
            id: items[i]?.id,
            category: items[i]?.category,
            score: s.score,
            reason: s.reason,
          })),
        };
      }
    } catch (e) {}
  }

  // ═══ v1.12.67 — colorAll (40% pair-wise + 60% bank) ═══
  const colorAll = 0.4 * colorS + 0.6 * bankColorS;

  // ═══ v1.12.69 — materialAll (40% pair-wise + 60% bank, même ratio que color) ═══
  const materialAll = 0.4 * materialPairS + 0.6 * bankMaterialS;

  // ═══ Pondération v1.12.70 — Option A (color = material) ═══
  // colorAll 22% + materialAll 22% + form 19% + weather 15% + style 22% = 100%
  // (vs v1.12.69 : colorAll 30% + materialAll 15%)
  // Color et material à égalité = tendance "quiet luxury" où la matière prime
  // autant que le visuel.
  const cohesion = (
    colorAll * 0.22
    + materialAll * 0.22
    + formS * 0.19
    + weatherS * 0.15
    + styleS * 0.22
  );

  return {
    total: cohesion,
    color: colorS,
    formality: formS,
    weather: weatherS,
    style: styleS,
    bankColor: bankColorS,
    colorAll,
    // Phase D.3 : exposé pour debug
    materialPair: materialPairS,
    bankMaterial: bankMaterialS,
    materialAll,
    _bankColorBreakdown: bankColorBreakdown,
    _bankMaterialBreakdown: bankMaterialBreakdown,
  };
}

// ─── MATCH contre un TAG donné (0-1) ───
// Formule priorisant les styleScores IA (v2) quand disponibles,
// fallback sur le matching keyword/formality (v1).
export function scoreOutfitAgainstTag(items, tag, guide) {
  if (!tag || !items?.length) return 0;
  const tagLower = tag.toLowerCase();

  const rule = guide?.occasions?.[tag]
    || guide?.occasions?.[tag.charAt(0).toUpperCase() + tag.slice(1)];
  const keywords = (rule?.keywords || [tagLower]).map(k => k.toLowerCase());
  const pieces = (rule?.typicalPieces || []).map(p => p.toLowerCase());
  const materials = (rule?.materials || []).map(m => m.toLowerCase());
  const formalityRange = rule?.formalityRange || [0, 10];

  // v1.12.26 : poids accessoires augmenté de 0.5 à 1.0 — un chapeau ou sac
  // mal accordé ne devait plus passer "sous le radar" du scoring.
  const CAT_IMPORTANCE = {
    Robes: 3, Hauts: 2.5, Bas: 2.5, Vestes: 2, Chaussures: 2.5,
    Sacs: 1, Bijoux: 1, Accessoires: 1
  };

  const isGenericTag = /^(casual|basic|tendance|trendy|moderne|everyday|polyvalent|daily)$/.test(tagLower);

  // ═══ v1.12.27 : Garde-fou per-item — logique INVERSÉE ═══
  // ═══ v1.12.41 : SOFTENED — pénalité douce au lieu de return 0 ═══
  //
  // Avant (v1.12.27) : un seul accessoire avec styleScores[tag] < 25 ou
  //   distToRange >= 3 → return 0 → outfit invisible. Sur tags non-tolérants
  //   (Chic, Bureau, Soirée, ...), tagMatches[tag]=0 → filtre LooksScreen
  //   >= 0.55 KO → chip vide.
  //
  // Maintenant : on accumule des pénalités douces (0.05/issue, cap 0.25)
  //   appliquées au baseScore final (juste avant return). L'outfit reste
  //   visible si autres scores bons mais classé plus bas. Préserve
  //   l'intention "warning quality" sans tuer la liste.
  //
  // TOLERANT_TAGS (Casual/Sport/Weekend/...) : pas de pénalité du tout.
  const TOLERANT_TAGS = [
    'casual', 'sport', 'weekend',
    'basic', 'everyday', 'polyvalent', 'daily',
    'tendance', 'trendy', 'moderne', 'streetwear',
  ];
  const isTolerantTag = TOLERANT_TAGS.includes(tagLower);

  let perItemPenalty = 0;
  if (!isTolerantTag) {
    // ═══ v1.12.29 : Écart formalité INTRA-OUTFIT >=3 (SOFT v1.12.42) ═══
    //
    // Sur tags stricts (Chic/Bureau/Soirée/Élégant/...), un écart de
    // formalité >= 3 entre items signale une tenue mixte (ex: sac
    // formality=4 + robe formality=7). Avant v1.12.42 : return 0 hard
    // reject. Maintenant : pénalité douce 0.05, intégrée dans le cap 0.25.
    const STRICT_GAP_THRESHOLD = 3;
    const allFormalities = items
      .map(it => typeof it.ai_metadata?.formality === 'number' ? it.ai_metadata.formality : null)
      .filter(f => f !== null);
    if (allFormalities.length >= 2) {
      const minF = Math.min(...allFormalities);
      const maxF = Math.max(...allFormalities);
      if (maxF - minF >= STRICT_GAP_THRESHOLD) {
        perItemPenalty += 0.05;
      }
    }

    const ACCESSORY_CATS = ['Sacs', 'Bijoux', 'Accessoires', 'Chaussures'];
    for (const it of items) {
      if (!ACCESSORY_CATS.includes(it.category)) continue;
      const meta = it.ai_metadata || {};

      // Test 1 : styleScores IA très bas pour ce tag
      const styleScore = meta.styleScores?.[tagLower]
        || meta.styleScores?.[tag.charAt(0).toUpperCase() + tag.slice(1)];
      if (typeof styleScore === 'number' && styleScore < 25) {
        perItemPenalty += 0.05;
      }

      // Test 2 : formalité avec écart trop grand par rapport au range cible
      // Ex: chapeau formality=2, range bureau=[5,8] → écart 3
      const formality = meta.formality;
      if (typeof formality === 'number') {
        const distToRange = formality < formalityRange[0]
          ? formalityRange[0] - formality
          : formality > formalityRange[1]
            ? formality - formalityRange[1]
            : 0;
        if (distToRange >= 3) {
          perItemPenalty += 0.05;
        }
      }
    }
    // Cap à 0.25 pour qu'un outfit baseline 0.7 + 5 issues reste à 0.45
    // (filtré sur le seuil 0.55 mais pas zéroé non plus)
    perItemPenalty = Math.min(0.25, perItemPenalty);
  }

  let totalWeight = 0;
  let scoreSum = 0;
  let hasDirectAITag = false;
  let hasStrongPieceMatch = false;
  let hasAIStyleScore = false;
  const formalities = [];

  for (const it of items) {
    const catWeight = CAT_IMPORTANCE[it.category] || 1;
    totalWeight += catWeight;

    const meta = it.ai_metadata || {};
    const name = (it.name || '').toLowerCase();
    const brand = (it.brand || '').toLowerCase();
    const fullText = `${name} ${brand}`;
    const itemTags = Array.isArray(meta.styleTags)
      ? meta.styleTags.map(t => (t || '').toLowerCase())
      : [];
    const material = (meta.material || '').toLowerCase();
    const formality = meta.formality;
    if (typeof formality === 'number') formalities.push(formality);

    let itemMatch = 0; // 0 à 1 pour cet item

    // ═══ PRIORITÉ 0 (v2) : styleScores IA directs ═══
    // Si l'IA a noté cet item sur le tag, on utilise directement son score
    // v1.12.19 : plafonné à 0.95 (au lieu de 1.0) pour éviter saturation
    const styleScores = meta.styleScores;
    if (styleScores && typeof styleScores === 'object') {
      const aiScore = styleScores[tagLower] || styleScores[tag.charAt(0).toUpperCase() + tag.slice(1)];
      if (typeof aiScore === 'number') {
        itemMatch = Math.max(itemMatch, Math.min(0.95, aiScore / 100));
        hasAIStyleScore = true;
        if (aiScore >= 80) hasDirectAITag = true;
      }
      // Bonus si le tag est présent dans occasions (secondary signal)
      const occScores = meta.occasions;
      if (occScores && typeof occScores[tagLower] === 'number' && occScores[tagLower] >= 70) {
        itemMatch = Math.max(itemMatch, Math.min(0.95, occScores[tagLower] / 100));
      }
    }

    // ═══ PRIORITÉ 1 (v1 fallback) : Tag IA DIRECT ═══
    // v1.12.19 : 1.0 → 0.85 (un tag exact ne doit pas saturer le score)
    if (itemTags.includes(tagLower)) {
      itemMatch = Math.max(itemMatch, 0.85);
      hasDirectAITag = true;
    }
    // Priorité 2 : Tag IA similaire via keywords
    else if (itemTags.some(t => keywords.some(kw => t.includes(kw) || kw.includes(t)))) {
      itemMatch = Math.max(itemMatch, 0.75); // 0.85 → 0.75
    }

    // Priorité 3 : Pièce typique dans le nom
    if (pieces.length > 0 && pieces.some(p => fullText.includes(p))) {
      itemMatch = Math.max(itemMatch, 0.75); // 0.85 → 0.75
      hasStrongPieceMatch = true;
    }

    // Priorité 4 : Matière typique
    if (materials.length > 0 && materials.some(m => material.includes(m) || fullText.includes(m))) {
      itemMatch = Math.max(itemMatch, 0.65); // 0.7 → 0.65
    }

    // Priorité 5 : Formalité dans le range
    if (typeof formality === 'number' && formality >= formalityRange[0] && formality <= formalityRange[1]) {
      itemMatch = Math.max(itemMatch, 0.55);
    }

    // Priorité 6 : Keyword dans le nom
    if (keywords.some(kw => fullText.includes(kw))) {
      itemMatch = Math.max(itemMatch, 0.55); // 0.6 → 0.55
    }

    // Pénalité tags génériques
    if (isGenericTag && itemMatch >= 0.85) itemMatch = 0.75;

    scoreSum += itemMatch * catWeight;
  }

  let baseScore = totalWeight === 0 ? 0 : scoreSum / totalWeight;

  // ═══ BONUS ═══
  // v1.12.19 : bonus drastiquement réduits pour éviter saturation à 1.0
  //   Avant : +0.10 + 0.05 + 0.05 = jusqu'à +20% ajoutés
  //   Après : +0.05 + 0.03 + 0.02 = jusqu'à +10% ajoutés
  // Combiné avec plafonds itemMatch v1.12.19, le baseScore restera entre
  // 0.55-0.95 typiquement (au lieu de 0.85-1.0 saturé).
  if (hasDirectAITag && !isGenericTag) {
    baseScore = Math.min(0.95, baseScore + 0.05);
  }
  if (hasStrongPieceMatch) {
    baseScore = Math.min(0.95, baseScore + 0.03);
  }
  if (formalities.length >= 2) {
    const avg = formalities.reduce((a, b) => a + b, 0) / formalities.length;
    if (avg >= formalityRange[0] && avg <= formalityRange[1]) {
      baseScore = Math.min(0.95, baseScore + 0.02);
    }
  }

  // v1.12.41 : pénalité per-item soft (cf. bloc TOLERANT_TAGS plus haut)
  if (perItemPenalty > 0) {
    baseScore = Math.max(0, baseScore - perItemPenalty);
  }

  // ═══ Bonus rappel couleur (Sacs/Bijoux/Accessoires) ═══
  // Un accessoire qui partage sa primaryColor avec un item CORE (Haut/Bas/
  // Robe/Veste/Chaussure) crée un rappel visuel. On bonifie le tagScore
  // pour que ce rappel pèse aussi dans le ranking par tag, pas seulement
  // dans cohesion.color. Sans ça, un sac "catalogué chic" pouvait écraser
  // un sac "moins chic mais visuellement assorti" sur l'axe tag dominant.
  // +0.02 par item CORE partageant la couleur, cap +0.04 par accessoire,
  // cap global +0.08.
  const CORE_FOR_ECHO = new Set(['Hauts', 'Bas', 'Robes', 'Vestes', 'Chaussures']);
  const ACCESS_FOR_ECHO = new Set(['Sacs', 'Bijoux', 'Accessoires']);
  let echoBonus = 0;
  for (const it of items) {
    if (!ACCESS_FOR_ECHO.has(it.category)) continue;
    const p = normalizeColor(it.ai_metadata?.primaryColor || it.color);
    if (!p) continue;
    let echoes = 0;
    for (const other of items) {
      if (other === it) continue;
      if (!CORE_FOR_ECHO.has(other.category)) continue;
      const q = normalizeColor(other.ai_metadata?.primaryColor || other.color);
      if (q && q === p) echoes++;
    }
    echoBonus += Math.min(0.04, echoes * 0.02);
  }
  echoBonus = Math.min(0.08, echoBonus);
  if (echoBonus > 0) {
    baseScore = Math.min(0.95, baseScore + echoBonus);
  }

  return baseScore;
}

// ─── Calcule tous les tag-matches d'un outfit (retour: { tag: 0.xx } ) ───
export function computeAllTagMatches(items, candidateTags, guide) {
  const matches = {};
  for (const tag of candidateTags) {
    matches[tag] = scoreOutfitAgainstTag(items, tag, guide);
  }
  return matches;
}

// ─── Signature stable d'un outfit (pour dédup) ───
// Basée STRICTEMENT sur les IDs items : garantit qu'une même combinaison
// d'items n'apparaît qu'une seule fois.
function outfitSignature(items) {
  if (!items || items.length === 0) return '';
  // ═══ FIX 2026-04 (v1.7.70) : signature ROBUSTE sans Math.random() ═══
  // Avant : fallback Math.random() en dernier recours → 2 mêmes outfits
  //   pouvaient avoir des signatures différentes → dédup impossible.
  // Maintenant : composite name+brand+category+price normalisé. Stable entre
  //   appels même si l'item n'a pas d'id (cas catalogue partenaire).
  const itemKey = (i) => {
    if (i.id) return `id:${i.id}`;
    if (i.product_id) return `pid:${i.product_id}`;
    const name  = String(i.name || '').toLowerCase().trim();
    const brand = String(i.brand || '').toLowerCase().trim();
    const cat   = String(i.category || '');
    const price = Math.round(Number(i.price) || 0);
    return `c:${cat}|${brand}|${name}|${price}`;
  };
  return items
    .map(itemKey)
    .filter(Boolean)
    .sort()
    .join('::');
}

// ─── GÉNÉRATION PRINCIPALE ───
// Retourne les outfits uniques avec leurs scores multi-tags
export function generateOutfits({ wardrobe, tempC, maxOutfits = 2000, candidateTags = [], guide = null, targetStyle = null, targetOccasion = null, colorBank = null, materialBank = null }) {
  if (!wardrobe || wardrobe.length === 0) return [];

  // v1.10.84 : Le targetStyle (préférence user) est passé à computeCohesion
  // pour vérifier que tous les items de l'outfit s'alignent sur ce style.
  // Si null, on tombe sur la logique de cohérence générale (basée sur le
  // meilleur axe consensuel).

  // Grouper par catégorie
  const byCategory = {
    Robes: [], Hauts: [], Bas: [], Vestes: [], Chaussures: [],
    Sacs: [], Bijoux: [], Accessoires: [],
  };

  // ═══ v1.12.7 (Fix C) : Pré-filtrer les items "à risque" si targetStyle ═══
  //
  // Quand l'utilisateur demande un style précis (ex: "chic"), on ne veut PAS
  // qu'un item non analysé par l'IA (sans styleScores) entre dans une tenue
  // structurelle (Robe/Haut/Bas/Veste/Chaussures/Sac) — risque trop élevé
  // d'incohérence visuelle.
  //
  // Pour les catégories DÉCORATIVES (Bijoux, Accessoires), le risque visuel
  // est faible donc on accepte même non analysés.
  //
  // Si moins de 50% du dressing est analysé, on désactive ce filtre pour
  // éviter d'avoir 0 outfit généré (cas dressing récent ou IA en cours).
  const STRUCTURAL_CATS = new Set(['Robes', 'Hauts', 'Bas', 'Vestes', 'Chaussures', 'Sacs']);
  const targetLowerForFilter = targetStyle ? String(targetStyle).toLowerCase() : null;

  // ═══ v1.12.10 (Fix sac à dos) : Filtre type-de-pièce selon occasion ═══
  //
  // Certains items sont stylistiquement OK pour un style mais inadaptés
  // à l'occasion. Cas typique : sac à dos transport — peut être très
  // minimaliste (épuré, monochrome) mais évoque la fonction (école,
  // voyage) plus que la mode.
  //
  // Règle : sac à dos / sac de sport / sacoche utilitaire ne sont gardés
  // QUE pour les occasions casual : weekend, casual, sport.
  // Pour bureau/soirée/cocktail/cérémonie/mariage/déjeuner → exclus.
  //
  // Détection : mots-clés dans le nom de l'item (compatible tous catalogues).
  const BACKPACK_REGEX = /\b(sac\s*[àa]?\s*dos|backpack|rucksack|cartable|sac\s+de\s+sport|sac\s+de\s+gym|sac\s+banane|fanny\s*pack|gym\s*bag|sport\s*bag|sac\s+de\s+voyage|sac\s+de\s+rando)\b/i;
  const CASUAL_OCCASIONS = new Set(['weekend', 'casual', 'sport', 'plage', 'voyage']);
  const occasionLowerForFilter = targetOccasion ? String(targetOccasion).toLowerCase() : null;
  // Filtre actif si user a choisi une occasion ET que ce n'est PAS une
  // occasion casual (= où le sac à dos est OK).
  const enableBackpackFilter = !!occasionLowerForFilter && !CASUAL_OCCASIONS.has(occasionLowerForFilter);
  let droppedBackpacks = 0;

  let totalItems = 0, analyzedItems = 0;
  for (const it of wardrobe) {
    totalItems++;
    if (it.ai_metadata?.styleScores && Object.keys(it.ai_metadata.styleScores).length > 0) {
      analyzedItems++;
    }
  }
  const analyzedRatio = totalItems > 0 ? analyzedItems / totalItems : 1;
  const enableStructuralFilter = !!targetLowerForFilter && analyzedRatio >= 0.5;

  let droppedUnanalyzed = 0;
  let droppedLowMatch = 0;

  for (const it of wardrobe) {
    if (!byCategory[it.category]) continue;

    // ═══ v1.12.10 : Exclure les sacs à dos / sacs sport / utilitaires
    // pour les occasions non-casual (bureau, soirée, etc.) ═══
    if (enableBackpackFilter && it.category === 'Sacs') {
      const name = String(it.name || '').toLowerCase();
      const desc = String(it.description || it.subcategory || '').toLowerCase();
      const fullText = `${name} ${desc}`;
      if (BACKPACK_REGEX.test(fullText)) {
        droppedBackpacks++;
        continue;
      }
    }

    if (enableStructuralFilter && STRUCTURAL_CATS.has(it.category)) {
      const ss = it.ai_metadata?.styleScores;
      // Pas de styleScores du tout → exclure
      if (!ss || typeof ss !== 'object' || Object.keys(ss).length === 0) {
        droppedUnanalyzed++;
        continue;
      }
      // Score sur le target trop faible → exclure aussi
      let scoreOnTarget = null;
      for (const k of Object.keys(ss)) {
        if (k.toLowerCase() === targetLowerForFilter) { scoreOnTarget = ss[k]; break; }
      }
      if (typeof scoreOnTarget === 'number' && scoreOnTarget < 25) {
        // < 25/100 = vraiment incompatible avec ce style → on exclut
        droppedLowMatch++;
        continue;
      }
    }

    byCategory[it.category].push(it);
  }

  if (enableStructuralFilter) {
    console.log(`[OutfitEngine] Pré-filtre target='${targetStyle}' : ${droppedUnanalyzed} items sans IA + ${droppedLowMatch} items <25/100 exclus`);
  } else if (targetLowerForFilter) {
    console.log(`[OutfitEngine] Pré-filtre target='${targetStyle}' désactivé (analyzedRatio=${(analyzedRatio*100).toFixed(0)}% < 50%)`);
  }
  if (enableBackpackFilter) {
    console.log(`[OutfitEngine] Filtre sacs utilitaires (occasion='${targetOccasion}') : ${droppedBackpacks} sacs à dos / sport exclus`);
  }

  const outfits = [];
  const seen = new Set();

  // Validation : une tenue DOIT avoir une structure minimale valide
  // Soit (robe + chaussure), soit (haut + bas + chaussure)
  const isValidOutfitStructure = (items) => {
    const cats = new Set(items.map(i => i.category));
    if (!cats.has('Chaussures')) return false; // chaussure obligatoire
    if (cats.has('Robes')) return true; // robe + chaussure ✓
    if (cats.has('Hauts') && cats.has('Bas')) return true; // haut + bas + chaussure ✓
    return false;
  };

  const addOutfit = (items) => {
    if (!isValidOutfitStructure(items)) return; // structure invalide → skip
    const sig = outfitSignature(items);
    if (seen.has(sig)) return;
    seen.add(sig);
    // v1.10.84 : passer targetStyle à computeCohesion pour aligner sur préf user
    const coh = computeCohesion(items, tempC, targetStyle, colorBank, materialBank);
    // v1.12.440 — aligné sur backend CDK round 6 :
    //  - formality < 0.25 (était === 0 — exige formality moyenne, pas juste non-nulle)
    //  - total < 0.36 (était 0.32 — exige cohésion globale décente)
    // Le frontend fallback local doit produire les mêmes outfits que le backend
    // pour cohérence visuelle quand l'app bascule (cache miss = computing 202).
    if (coh.formality < 0.25) return;
    if (coh.total < 0.36) return;
    outfits.push({ items, cohesion: coh, signature: sig });
  };

  const needVeste = tempC < 15;
  const needLight = tempC > 22;

  // ═══ v1.9.38 : ACC_VARIANTS_LIMIT 30 (au lieu de 8) ═══
  // Plus le pool est grand, plus la rotation des accessoires diversifie les outfits.
  // Avec 100 outfits générés et 30 sacs/bijoux dispo, chaque sac apparaît ~3 fois
  // (au lieu de 12-13 avec 8 dispo). Diversité visuelle bien meilleure.
  // v1.10.32 : pool d'accessoires augmenté pour vraie diversité
  //   Avant : 30 par catégorie → forte concentration sur les top items.
  //   Maintenant : 100 par catégorie (et tous les Bas/Hauts/Chaussures car
  //   ces structurels passent par la rotation explicite ailleurs).
  const ACC_VARIANTS_LIMIT = 100;
  const sacVariants    = byCategory.Sacs.slice(0, ACC_VARIANTS_LIMIT);
  const bijouVariants  = byCategory.Bijoux.slice(0, ACC_VARIANTS_LIMIT);
  const vesteVariants  = byCategory.Vestes.slice(0, ACC_VARIANTS_LIMIT);
  const accVariants    = (byCategory.Accessoires || []).slice(0, ACC_VARIANTS_LIMIT);
  // Si pas d'accessoire dispo, on met `[null]` pour que la boucle tourne 1 fois sans ajouter
  const sacChoices    = sacVariants.length    > 0 ? sacVariants    : [null];
  const bijouChoices  = bijouVariants.length  > 0 ? bijouVariants  : [null];
  const vesteChoices  = (needVeste && vesteVariants.length > 0) ? vesteVariants : [null];
  const accChoices    = accVariants.length    > 0 ? accVariants    : [null];

  // ═══ v1.9.33 : Génération en PASSES pour garantir la diversité de hero items ═══
  //
  //   Problème ancien : la double boucle imbriquée générait toutes les variantes
  //     d'accessoires d'UNE SEULE robe avant de passer à la suivante. Avec
  //     maxOutfits=2000 et 8 chaussures × 8 sacs × 8 bijoux × 8 vestes = 4096
  //     variantes par robe, l'algo restait bloqué sur les premières robes.
  //
  //   Solution : on fait 2 passes :
  //     PASSE 1 : 1 outfit par robe/haut unique avec accessoires de base.
  //               → Garantit que CHAQUE robe/haut du catalogue a au moins 1 outfit.
  //     PASSE 2 : si on a encore de la marge dans maxOutfits, on génère des
  //               variantes d'accessoires (pour avoir du choix au sein d'un hero).
  //
  //   Bénéfice : avec un catalogue de 100 robes, on aura au moins 100 outfits
  //     visuellement distincts (au lieu de 5-10 avec l'ancienne version).

  // ── Helper : génère un outfit avec accessoires VARIÉS (rotation) ──
  // ═══ v1.10.32 : Index INDÉPENDANTS par catégorie + décalage par item principal ═══
  //
  //   Avant (v1.9.38) : un seul index `_accIdx` partagé pour sac/bijou/veste.
  //     Conséquence : outfits = (sac[0]+bijou[0]+veste[0]), (sac[1]+bijou[1]+veste[1])...
  //     Si 5 sacs, 3 bijoux, 2 vestes → après 2 outfits, vestes répètent ;
  //     après 3 outfits, bijoux répètent. Combinaisons possibles non explorées.
  //
  //   Maintenant : 1 index par catégorie + un offset basé sur l'item principal.
  //     Outfit 1 (haut H1) : sac[0+1]   bijou[0+1]   veste[0+1]   accessoire[0+1]
  //     Outfit 2 (haut H2) : sac[2+1]   bijou[2+1]   veste[2+1]   accessoire[2+1]
  //     Etc. Les indices se décorrèlent → toutes les combinaisons sont explorées.
  //
  //   On garantit aussi qu'un même sac/bijou/veste ne se répète pas trop d'affilée
  //   en multipliant l'index par un nombre premier différent par catégorie.
  let _sacIdx = 0, _bijouIdx = 0, _vesteIdx = 0, _accGenIdx = 0;
  const buildOutfitWithBestAccessories = (mainItems, mainOffset = 0) => {
    const outfit = [...mainItems];
    // Décalage par item principal pour décorréler les rotations
    const sacIdx = sacChoices.length > 0 ? (_sacIdx + mainOffset * 7)  % sacChoices.length  : 0;
    const bijouIdx = bijouChoices.length > 0 ? (_bijouIdx + mainOffset * 11) % bijouChoices.length : 0;
    const vesteIdx = vesteChoices.length > 0 ? (_vesteIdx + mainOffset * 13) % vesteChoices.length : 0;
    const accIdx   = accChoices.length > 0 ? (_accGenIdx + mainOffset * 17) % accChoices.length   : 0;
    if (sacChoices[sacIdx])     outfit.push(sacChoices[sacIdx]);
    if (bijouChoices[bijouIdx]) outfit.push(bijouChoices[bijouIdx]);
    if (vesteChoices[vesteIdx]) outfit.push(vesteChoices[vesteIdx]);
    if (accChoices[accIdx])     outfit.push(accChoices[accIdx]);
    _sacIdx++; _bijouIdx++; _vesteIdx++; _accGenIdx++;
    return outfit;
  };

  // ═══ PASSE 1A : Robe → 1 outfit par robe avec meilleurs accessoires ═══
  //
  // v1.10.12 : Rotation des chaussures par robe pour éviter qu'une seule
  // chaussure (la 1re du catalogue) soit utilisée pour TOUTES les robes.
  // On démarre l'itération à un index différent pour chaque robe.
  outer1a:
  for (let robeIdx = 0; robeIdx < byCategory.Robes.length; robeIdx++) {
    const robe = byCategory.Robes[robeIdx];
    if (outfits.length >= maxOutfits) break;
    // Décale le point de départ des chaussures pour cette robe
    const chaussuresLen = byCategory.Chaussures.length;
    if (chaussuresLen === 0) continue;
    for (let i = 0; i < chaussuresLen; i++) {
      const chaussureIdx = (robeIdx + i) % chaussuresLen;
      const chaussure = byCategory.Chaussures[chaussureIdx];
      const outfit = buildOutfitWithBestAccessories([robe, chaussure], robeIdx);
      const sigBefore = outfits.length;
      addOutfit(outfit);
      if (outfits.length > sigBefore) break; // l'outfit a été accepté (cohésion OK)
    }
    if (outfits.length >= maxOutfits) break outer1a;
  }

  // ═══ PASSE 1B : Haut+Bas → outfits par Haut avec rotation des bas ═══
  //
  // v1.10.30 : Au lieu de générer 1 seul outfit par Haut (toujours avec le bas[0]),
  //   on génère 1 outfit pour CHAQUE bas du catalogue avec rotation par index.
  //   Ça produit `Hauts × Bas` outfits maximum, ce qui après dédup heroKey
  //   (HAUT|BAS) donne autant de combinaisons distinctes que possible.
  //
  // Avant : 50 Hauts × 1 outfit/Haut = 50 outfits, tous avec Bas[0] → boucle.
  // Maintenant : 50 Hauts × 5 Bas = 250 outfits avec couples (haut, bas) variés.
  outer1b:
  for (let hautIdx = 0; hautIdx < byCategory.Hauts.length; hautIdx++) {
    const haut = byCategory.Hauts[hautIdx];
    if (outfits.length >= maxOutfits) break;
    const chaussuresLen = byCategory.Chaussures.length;
    const basLen = byCategory.Bas.length;
    if (chaussuresLen === 0 || basLen === 0) continue;

    // Pour ce Haut, on essaie chaque Bas (avec rotation pour varier le point de départ)
    for (let bIdx = 0; bIdx < basLen; bIdx++) {
      // Rotation : décaler par hautIdx pour que les premiers hauts ne se mappent
      // pas tous au même bas[0]
      const basIdx = (hautIdx + bIdx) % basLen;
      const bas = byCategory.Bas[basIdx];

      // Pour ce couple (haut, bas), trouver une chaussure (rotation aussi)
      let placed = false;
      for (let cIdx = 0; cIdx < chaussuresLen; cIdx++) {
        const chaussureIdx = (hautIdx + bIdx + cIdx) % chaussuresLen;
        const chaussure = byCategory.Chaussures[chaussureIdx];
        const outfit = buildOutfitWithBestAccessories([haut, bas, chaussure], hautIdx * basLen + bIdx);
        const sigBefore = outfits.length;
        addOutfit(outfit);
        if (outfits.length > sigBefore) { placed = true; break; }
      }
      if (outfits.length >= maxOutfits) break outer1b;
      // On continue à essayer les autres Bas pour ce Haut (pas de break ici !)
    }
  }

  // ═══ PASSE 2 : Variantes d'accessoires si on a encore de la marge ═══
  //   On itère maintenant les variantes (chaussures × sacs × bijoux × vestes × accessoires)
  //   pour TOUTES les robes/hauts. Cela permet aux groupes de dédup côté UI
  //   de choisir la meilleure variante d'accessoires.
  //
  // v1.10.32 : Accessoires (ceintures/écharpes/etc.) inclus + ordre des boucles
  //   inversé pour favoriser la diversité des vestes/accessoires plus tôt.
  //
  //   STRATÉGIE 1 : Robe + Chaussure (avec variantes d'accessoires)
  outer1:
  for (const robe of byCategory.Robes) {
    if (outfits.length >= maxOutfits) break;
    for (const chaussure of byCategory.Chaussures) {
      for (const veste of vesteChoices) {
        for (const acc of accChoices) {
          for (const bijou of bijouChoices) {
            for (const sac of sacChoices) {
              const outfit = [robe, chaussure];
              if (sac)   outfit.push(sac);
              if (bijou) outfit.push(bijou);
              if (veste) outfit.push(veste);
              if (acc)   outfit.push(acc);
              addOutfit(outfit);
              if (outfits.length >= maxOutfits) break outer1;
            }
          }
        }
      }
    }
  }

  // STRATÉGIE 2 : Haut + Bas + Chaussure (avec variantes d'accessoires)
  outer2:
  for (const haut of byCategory.Hauts) {
    if (outfits.length >= maxOutfits) break;
    for (const bas of byCategory.Bas) {
      for (const chaussure of byCategory.Chaussures) {
        for (const veste of vesteChoices) {
          for (const acc of accChoices) {
            for (const bijou of bijouChoices) {
              for (const sac of sacChoices) {
                const outfit = [haut, bas, chaussure];
                if (sac)   outfit.push(sac);
                if (bijou) outfit.push(bijou);
                if (veste) outfit.push(veste);
                if (acc)   outfit.push(acc);
                addOutfit(outfit);
                if (outfits.length >= maxOutfits) break outer2;
              }
            }
          }
        }
      }
    }
  }

  // ═══ STRATÉGIE 3 : SUPPRIMÉE — les variantes sont déjà gérées dans S1+S2 ═══

  // ═══ SCORING MULTI-TAGS + TENDANCE ═══
  // v1.12.7 : si targetStyle est fourni (préf user), le score utilise
  // tagMatches[targetStyle] au lieu du meilleur tag. Cela aligne le
  // tri sur ce que l'utilisateur veut, pas sur le style le plus marqué.
  const targetLower = targetStyle ? String(targetStyle).toLowerCase() : null;

  for (const o of outfits) {
    o.tagMatches = candidateTags.length > 0
      ? computeAllTagMatches(o.items, candidateTags, guide)
      : {};
    const bestTagMatch = Object.values(o.tagMatches).reduce((a, b) => Math.max(a, b), 0);

    // v1.12.7 : score sur le target si fourni
    let targetTagMatch = bestTagMatch;
    if (targetLower) {
      const direct = o.tagMatches[targetLower] ?? o.tagMatches[targetStyle];
      if (typeof direct === 'number') {
        targetTagMatch = direct;
      } else {
        // Le candidateTags ne contient pas le target ? On le calcule à la volée
        targetTagMatch = scoreOutfitAgainstTag(o.items, targetStyle, guide);
      }
    }

    // Score tendance : moyenne des trendScore des items (pondérée par catégorie)
    let trendSum = 0, trendWeight = 0, trendyItemsCount = 0;
    const CAT_W = { Robes: 3, Hauts: 2.5, Bas: 2.5, Vestes: 2, Chaussures: 2.5, Sacs: 1, Bijoux: 0.5, Accessoires: 0.5 };
    for (const it of o.items) {
      const w = CAT_W[it.category] || 1;
      const trend = it.ai_metadata?.trendScore;
      const isTrendy = it.ai_metadata?.isTrendy;
      if (typeof trend === 'number') {
        trendSum += trend * w;
        trendWeight += w;
      }
      if (isTrendy === true) trendyItemsCount++;
    }
    const avgTrend = trendWeight > 0 ? (trendSum / trendWeight) / 100 : 0.5; // 0-1
    o.trendScore = Math.round(avgTrend * 100);
    o.trendyItemsCount = trendyItemsCount;

    // ═══ v1.12.19 : Rescale agressive pour étaler vraiment ═══
    //
    // Avec les fixes v1.12.19 (plafonds itemMatch 0.85, bonus réduits),
    // les scores raw seront typiquement entre 0.50 et 0.85 (au lieu de
    // 0.85-1.0 saturé).
    //
    // Distribution raw attendue :
    //   raw 0.50 → mauvaise (rejetée si < 0.45)
    //   raw 0.60 → moyenne
    //   raw 0.70 → bonne
    //   raw 0.80 → très bonne
    //   raw 0.85 → excellente (rare)
    //
    // Mapping demandé :
    //   raw 0.50 → score 50
    //   raw 0.65 → score 65
    //   raw 0.80 → score 85
    //   raw 0.85 → score 90 (max effectif)
    //
    // Soit : pente 1× sur [0.50-0.65], pente ~1.3× sur [0.65-0.85]
    const tagScore = targetTagMatch || 0.7;
    const cohScore = o.cohesion?.total ?? 0.7;

    // Pondération inversée vs v1.12.76 : cohesion 50% / bestTag 42% / trend 8%.
    // Avant : bestTag 50% écrasait cohesion 42% → un sac "catalogué chic"
    // mais discordant en palette pouvait sortir devant un sac visuellement
    // assorti. Le poids cohesion dominant rééquilibre vers l'harmonie globale
    // (color + material + style + form + weather) sans tuer l'alignement tag.
    const rawScore = (cohScore * 0.50 + tagScore * 0.42 + avgTrend * 0.08);

    // v1.12.76 — Scoring LINÉAIRE pour transparence : score = rawScore × 100.
    // Avant : étirement 0.65→65, 0.85→100 → un raw 0.82 (cohesion 84% + bestTag
    // 86%) sortait à 95+, et avec bank bonus on saturait à 100, ce qui ne
    // correspondait pas aux sous-scores visibles → confusion user.
    // Maintenant : 100% n'est atteint QUE si TOUS les composants sont au max.
    // - cohesion 84% + bestTag 86% + trend 50% → raw 0.82 → score 82 ✓
    // - cohesion 95% + bestTag 95% + trend 80% → raw 0.92 → score 92
    // - cohesion 100% + bestTag 100% + trend 100% → raw 1.00 → score 100
    o.score = Math.max(0, Math.min(100, Math.round(rawScore * 100)));

    // v1.12.7 : pénalité supplémentaire si target fourni et match faible
    // (un outfit avec target=chic mais tagMatches.chic=0.4 ne devrait pas
    // remonter en haut juste parce qu'il a tagMatches.sport=0.9)
    if (targetLower && targetTagMatch < 0.55) {
      o.score = Math.round(o.score * 0.5); // -50% sur le score final
    }

    o.bestTag = Object.entries(o.tagMatches).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    // v1.12.7 : on expose le score sur le target user pour debug/affichage
    o.targetTagMatch = targetTagMatch;
  }

  // ═══ Trier par score global, garder les top maxOutfits ═══
  outfits.sort((a, b) => b.score - a.score);
  const top = outfits.slice(0, maxOutfits);

  // Assigner un id stable
  return top.map((o, idx) => ({
    ...o,
    id: `outfit_v2_${idx}_${o.signature.slice(0, 40)}`,
  }));
}

// ─── Grouper les outfits par tag selon seuil de match (75% par défaut) ───
export function groupOutfitsByTag(outfits, tags, threshold = 0.82) {
  const groups = { 'Tout': outfits };
  for (const tag of tags) {
    groups[tag] = outfits.filter(o => (o.tagMatches?.[tag] || 0) >= threshold);
  }
  return groups;
}
